/*
 * What a reminder points at, and the one thing you would want to do about it.
 *
 * A reminder already carries target_type and target_id — the fasting alarm has
 * set them since the day it shipped — but nothing ever read them back. So the
 * notification arrived, he tapped it, and the app showed him a line of text
 * about a fast he then had to go and end by hand in another tab.
 *
 * This is the read-back. One resolver for what the thing is called, and one
 * action per kind where a single obvious action exists. Kinds without one are
 * not given a made-up button; they show what they are and stop there.
 */

import { supabase } from './supabase.js'
import { setPayableStatus } from './db.js'
import { setTaskDone, setItemDone } from './notes.js'
import { endFast } from './fasting.js'

/* Columns needed to name the thing and to act on it. Deliberately small: this
   runs when a sheet opens, in front of him. */
const TARGETS = {
  fast: {
    label: 'Fast',
    table: 'fasts',
    cols: 'id, started_at, ended_at, target_hours',
    name: () => 'Fasting',
    detail: (r) => (r.ended_at ? 'Ended' : 'Running'),
    action: (r) => (r.ended_at ? null : { label: 'End fast', run: (row) => endFast(row) }),
  },
  payable: {
    label: 'Payable',
    table: 'payables',
    /* to_account_id and build_id are not shown anywhere here, but
       setPayableStatus needs them: ticking a payable is the same one event
       that logs a build payment and pushes to savings. Selecting a short row
       would quietly skip both. */
    cols: 'id, name, amount, status, payout_id, to_account_id, build_id',
    name: (r) => r.name,
    detail: (r) => (r.status === 'Paid' ? 'Paid' : 'Unpaid'),
    action: (r) =>
      r.status === 'Paid' ? null : { label: 'Mark paid', run: (row) => setPayableStatus(row, true) },
  },
  task: {
    label: 'Task',
    table: 'tasks',
    cols: 'id, body, status, due_on',
    name: (r) => r.body,
    detail: (r) => (r.status === 'Done' ? 'Done' : 'Open'),
    action: (r) =>
      r.status === 'Done' ? null : { label: 'Mark done', run: (row) => setTaskDone(row, true) },
  },
  project_item: {
    label: 'Board item',
    table: 'project_items',
    cols: 'id, body, status',
    name: (r) => r.body,
    detail: (r) => (r.status === 'Done' ? 'Done' : 'Open'),
    action: (r) =>
      r.status === 'Done' ? null : { label: 'Mark done', run: (row) => setItemDone(row, true) },
  },
  project: {
    label: 'Project',
    table: 'projects',
    cols: 'id, title, closed_at',
    name: (r) => r.title,
    detail: (r) => (r.closed_at ? 'Closed' : null),
    action: () => null,
  },
  build: {
    label: 'Build',
    table: 'builds',
    cols: 'id, name, status',
    name: (r) => r.name,
    detail: (r) => (r.status === 'Completed' ? 'Done' : 'Raising'),
    action: () => null,
  },
}

/**
 * Resolve a reminder's target into something showable.
 *
 * Returns null when the reminder points at nothing, at a kind with no entry
 * here, or at a row that has since been deleted. A dangling target is not an
 * error worth showing him — the reminder still stands on its own.
 */
export async function resolveTarget(reminder) {
  const kind = TARGETS[reminder?.target_type]
  if (!kind || !reminder.target_id) return null

  const { data, error } = await supabase
    .from(kind.table)
    .select(kind.cols)
    .eq('id', reminder.target_id)
    .maybeSingle()
  if (error || !data) return null

  let detail = null
  try { detail = kind.detail?.(data) ?? null } catch { detail = null }

  return {
    label: kind.label,
    name: kind.name(data) || kind.label,
    detail,
    row: data,
    action: kind.action?.(data) ?? null,
  }
}

/* ── Rescheduling ───────────────────────────────────────────────
 * Moving a reminder clears sent_at. That is the whole point: a reminder he
 * pushed to tomorrow has to fire tomorrow, and the sweep only looks at rows
 * where sent_at is null.
 * ─────────────────────────────────────────────────────────────── */

/** Tonight, or the same clock time tomorrow, without dragging in a date library. */
export function laterToday(now = new Date()) {
  const d = new Date(now)
  d.setHours(d.getHours() + 3, 0, 0, 0)
  return d
}

export function tomorrow(now = new Date()) {
  const d = new Date(now)
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d
}

export async function rescheduleReminder(id, when) {
  const { error } = await supabase
    .from('reminders')
    .update({
      remind_at: new Date(when).toISOString(),
      /* Both cleared. sent_at so the sweep pushes it again at the new time,
         done_at because moving a reminder is the opposite of finishing it. */
      sent_at: null,
      done_at: null,
    })
    .eq('id', id)
  if (error) throw error
}

export async function setReminderNote(id, note) {
  const { error } = await supabase
    .from('reminders')
    .update({ note: note.trim() || null })
    .eq('id', id)
  if (error) throw error
}

export async function deleteReminder(id) {
  const { error } = await supabase.from('reminders').delete().eq('id', id)
  if (error) throw error
}

/** datetime-local wants local wall time, not an ISO instant. */
export function toLocalInput(iso) {
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
}
